import { useFormBuilder } from '@gov-nx/core/service';
import {
	type RezaChangeData,
	type RezaChangeFields,
	useRezaChangeFormDefinition,
} from '../..';
import { useRezaSwitcherLocale } from './useRezaSwitcherLocale';
import type { RezaOpravneniDto } from '@gov-nx/api';
import { useEffect } from 'react';
import type { UseRezaChangeOptionsResult } from './useRezaChangeOptions';

export const REZA_CHANGE_FORM_ID = 'reza-change-form';

interface useRezaChangeFormProps extends Pick<
	UseRezaChangeOptionsResult,
	'rezaList' | 'options' | 'singleOptionAsText' | 'isRepresentationAllowed'
> {
	setActiveSwitcherReza: (activeSwitcherReza: RezaOpravneniDto | null) => void;
	/**
	 * Currently active representation. When provided, the form is pre-filled with
	 * it so the user sees their previous selection when reopening the modal,
	 * instead of the form being reset to its default state.
	 */
	activeSwitcherReza?: RezaOpravneniDto | null;
	onAfterSubmit?: () => void;
	/**
	 * If true, the form will automatically submit when the 'role' or 'rezaCode' fields change.
	 * Useful for instant updates without requiring a manual submit action.
	 */
	autoSubmitOnChange?: boolean;
}

export const useRezaChangeForm = ({
	onAfterSubmit,
	autoSubmitOnChange = false,
	setActiveSwitcherReza,
	activeSwitcherReza,
	rezaList,
	options,
	singleOptionAsText,
	isRepresentationAllowed,
}: useRezaChangeFormProps) => {
	const { t } = useRezaSwitcherLocale();

	// Pre-fill the form with the currently active representation so that the
	// user sees their previous selection when reopening the modal, instead of
	// the form being reset to its default ("without representation") state.
	// Only honor the active code if it still exists in the current eligible
	// options — otherwise the submit path would resolve it to null and silently
	// clear the active representation.
	const activeRezaCodeIfEligible =
		activeSwitcherReza?.kod &&
		options.some((option) => option.value === activeSwitcherReza.kod)
			? activeSwitcherReza.kod
			: undefined;

	const defaultRezaCode =
		activeRezaCodeIfEligible ??
		singleOptionAsText?.singleOptionValue ??
		options[0]?.value ??
		undefined;

	const defaultRole = activeRezaCodeIfEligible
		? 'v_zastoupeni'
		: 'bez_zastoupeni';

	const [roleField, rezaCode] = useRezaChangeFormDefinition({
		isRepresentationAllowed,
		options,
		fields: {
			role: {
				label: t('form.label.role'),
				withoutRepresentationLabel: t(
					'form.option.role-without-representation'
				),
				withRepresentationLabel: t('form.option.role-with-representation'),
				defaultValue: defaultRole,
			},
			rezaCode: {
				label: t('form.label.reza-code'),
				requiredMessage: t('form.error.reza-code-required'),
				defaultValue: defaultRezaCode,
			},
		},
	});

	const form = useFormBuilder<RezaChangeData, RezaChangeFields>([
		roleField,
		rezaCode,
	]);

	const onSubmit = form.formMethods.handleSubmit((data) => {
		if (data.role === 'bez_zastoupeni') {
			setActiveSwitcherReza(null);
			onAfterSubmit?.();
			return;
		}

		const selectedCode = data.rezaCode ?? rezaCode.defaultValue;
		const activeReza =
			rezaList.data?.seznam?.find((reza) => reza.kod === selectedCode) ?? null;

		setActiveSwitcherReza(activeReza);
		onAfterSubmit?.();
	});

	useEffect(() => {
		if (!autoSubmitOnChange) return;
		const subscription = form.formMethods.watch(
			({ role, rezaCode }, { name }) => {
				if (role === 'v_zastoupeni' && !rezaCode) {
					return;
				}
				if (name === 'role' || name === 'rezaCode') {
					onSubmit();
				}
			}
		);
		return () => subscription.unsubscribe();
	}, [autoSubmitOnChange, onSubmit, form.formMethods]);

	return {
		form,
		onSubmit,
		role: form.formMethods.watch('role'),
		strings: {
			representationNotAllowed: t('form.message.representation-not-allowed'),
			representationListNotFound: t(
				'form.message.representation-list-not-found'
			),
			submitButton: t('form.button.submit'),
		},
	};
};
